import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Tabs } from '../../../ds/components';
import type { FinancingRecord } from '../types';
import { StageBar } from '../ui/StageBar';
import { StagePill } from '../ui/StagePill';
import styles from './record.module.css';

/** Props of every tab page of the financing record (F2's Unterlagen/Angebote/Übersicht mount with them). */
export interface FinancingTabProps {
  financing: FinancingRecord;
  onChanged(): void;
}

export type RecordTab = 'overview' | 'documents' | 'offers' | 'self-disclosure' | 'history';

export const RECORD_TABS: readonly { value: RecordTab; label: string }[] = [
  { value: 'overview', label: 'Übersicht' },
  { value: 'documents', label: 'Unterlagen' },
  { value: 'offers', label: 'Angebote' },
  { value: 'self-disclosure', label: 'Selbstauskunft' },
  { value: 'history', label: 'Verlauf' },
];

export function isRecordTab(value: string | null): value is RecordTab {
  return RECORD_TABS.some((tab) => tab.value === value);
}

export interface RecordFrameProps {
  financing: FinancingRecord;
  tab: RecordTab;
  onTab: (tab: RecordTab) => void;
  /** The header buttons (team: F1's, financier: F2's `FinancierHeaderActions` plus the overflow). */
  actions: ReactNode;
  children: ReactNode;
}

/**
 * The frame of the financing record (Finanzierungen 06/07/08, Finanzierer 03–11): breadcrumb „Finanzierungen /
 * FIN-0142", title with the stage pill, subline, header actions, the stage bar and the five tabs (`?tab=`).
 * The tab pages render the content area below it.
 */
export function RecordFrame({ financing, tab, onTab, actions, children }: RecordFrameProps) {
  const navigate = useNavigate();
  return (
    <div className={styles.page}>
      <div className={styles.top}>
        <div className={styles.head}>
          <div className={styles.headText}>
            <nav className={styles.crumbs} aria-label="Pfad">
              <button type="button" className={styles.crumbLink} onClick={() => void navigate('/financing')}>
                Finanzierungen
              </button>
              <span aria-hidden="true">/</span>
              <span className={styles.crumbNumber}>{financing.numberLabel}</span>
            </nav>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{financing.title}</h1>
              <StagePill stage={financing.stage} />
            </div>
            <div className={styles.subline}>{financing.subline}</div>
          </div>
          <div className={styles.headActions}>{actions}</div>
        </div>
        <StageBar stageBar={financing.stageBar} lostSegment={financing.lostSegment} />
        <Tabs
          label="Finanzierungsakte"
          items={RECORD_TABS}
          value={tab}
          onChange={onTab}
          panelId="financing-tab"
          className={styles.tabs}
        />
      </div>
      <div
        id={`financing-tab-${tab}`}
        role="tabpanel"
        aria-label={RECORD_TABS.find((t) => t.value === tab)?.label}
      >
        {children}
      </div>
    </div>
  );
}
